import {
    exportMonthClosing,
    getMonthClosing,
    type MonthClosingLine,
    type MonthClosingOpenReason,
    type FinancialDashboardScope,
    type MonthClosingResponse,
    type MonthClosingType,
} from '@/lib/api/financial/financial-dashboard-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import SalesSummaryTable from '@/pages/admin/financial/dashboard/components/sales-summary-table'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { MdClose, MdLinkOff, MdPictureAsPdf, MdRefresh, MdTableChart, MdTextSnippet, MdViewList, MdWorkspaces } from 'react-icons/md'
import { toast } from 'react-toastify'
import { DetailPopupProvider, useDetailPopup, type DetailType } from '@/components/detail-popup'

/**
 * Fechamento do mês: a tabela "O que foi vendido" do dashboard financeiro aberta em
 * item de catálogo (agrupado, o padrão) ou atendimento por atendimento, mais os Pix
 * que entraram sem atendimento.
 *
 * Sem filtro de propósito. A contabilidade somava as exportações de consultas, exames e
 * procedimentos e o total não batia com o dashboard: cada aba com seu recorte, sem os
 * Pix soltos. Aqui o recorte é um só, o da tabela, e o total é o dela por construção
 * (o backend ajusta os centavos para as linhas fecharem). Só o período muda.
 */

type PeriodMode = 'current' | 'month' | 'custom'
type ViewMode = 'grouped' | 'lines'

const MONTHS = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

/** Tipo do popup de detalhe (o mesmo das outras abas dos relatórios). */
const DETAIL_TYPE: Record<MonthClosingType, DetailType> = {
    CONSULTATION: 'consultation',
    EXAM: 'exam',
    ORDER: 'order',
}

/** Motivo do saldo em aberto: rótulo e cor. Convênio é regra, não pendência. */
const OPEN_REASON: Record<MonthClosingOpenReason, { label: string; className: string }> = {
    HEALTH_PLAN: { label: 'Convênio', className: 'badge-info' },
    OVERDUE: { label: 'Cobrança vencida', className: 'badge-error' },
    PENDING: { label: 'Cobrança pendente', className: 'badge-warning' },
    PARTIAL: { label: 'Pago em parte', className: 'badge-warning' },
    REFUNDED: { label: 'Estornado', className: 'badge-secondary' },
    NO_BILLING: { label: 'Sem cobrança', className: 'badge-neutral' },
    CANCELLED_CHARGE: { label: 'Cobrança cancelada', className: 'badge-neutral' },
    OTHER: { label: 'Outro', className: 'badge-ghost' },
}

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)

const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' }).format(new Date(iso)) : '—'

const formatShortDate = (iso: string) =>
    new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' }).format(new Date(iso))

/** Cor do círculo do tipo: a mesma em todas as linhas, para bater o olho. */
const TYPE_BADGE_CLASS: Record<MonthClosingType, string> = {
    CONSULTATION: 'bg-info/15 text-info',
    EXAM: 'bg-accent/15 text-accent',
    ORDER: 'bg-secondary/15 text-secondary',
}

const pad = (n: number) => String(n).padStart(2, '0')

/** `YYYY-MM-DD` do último dia do mês (mês 1-12). */
const lastDayOf = (year: number, month: number) =>
    `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`

interface MonthClosingSectionProps {
    scope: FinancialDashboardScope
}

/**
 * O popup de detalhe (consulta, exame, procedimento, cobrança) é o mesmo das outras abas.
 * Ao fechá-lo a tela recarrega: o operador costuma abrir para dar baixa.
 */
export default function MonthClosingSection({ scope }: MonthClosingSectionProps) {
    const [detailClosed, setDetailClosed] = useState(0)
    return (
        <DetailPopupProvider onClose={() => setDetailClosed(n => n + 1)}>
            <MonthClosingContent scope={scope} reloadSignal={detailClosed} />
        </DetailPopupProvider>
    )
}

function MonthClosingContent({ scope, reloadSignal }: MonthClosingSectionProps & { reloadSignal: number }) {
    const { openDetail } = useDetailPopup()
    /** Lista do "A receber": um tipo, ou todos (`TOTAL`). */
    const [openList, setOpenList] = useState<MonthClosingType | 'TOTAL' | null>(null)
    const labels = useServiceLabel()
    const { ClientLabel } = useClientLabel()
    const today = new Date()

    const [periodMode, setPeriodMode] = useState<PeriodMode>('current')
    const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1)
    const [selectedYear, setSelectedYear] = useState(today.getFullYear())
    const [customStart, setCustomStart] = useState(
        `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`
    )
    const [customEnd, setCustomEnd] = useState(
        `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
    )
    const [view, setView] = useState<ViewMode>('grouped')
    const [data, setData] = useState<MonthClosingResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadKey, setReloadKey] = useState(0)

    const typeLabel: Record<MonthClosingType, string> = {
        CONSULTATION: labels.Consultation,
        EXAM: labels.Exam,
        ORDER: labels.Order,
    }

    // Anos do seletor: o atual e os três anteriores.
    const years = useMemo(
        () => Array.from({ length: 4 }, (_, i) => today.getFullYear() - i),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    const params = useMemo(() => {
        if (periodMode === 'current') return { period: 'month' as const, scope }
        if (periodMode === 'month') {
            return {
                period: 'custom' as const,
                startDate: `${selectedYear}-${pad(selectedMonth)}-01`,
                endDate: lastDayOf(selectedYear, selectedMonth),
                scope,
            }
        }
        return { period: 'custom' as const, startDate: customStart, endDate: customEnd, scope }
    }, [periodMode, selectedMonth, selectedYear, customStart, customEnd, scope])

    const customInvalid = periodMode === 'custom' && (!customStart || !customEnd || customStart > customEnd)

    useEffect(() => {
        if (customInvalid) return
        let cancelled = false
        setLoading(true)
        setError(null)
        getMonthClosing(params)
            .then(response => {
                if (cancelled) return
                if (response.success && response.result) setData(response.result)
                else setError(response.error || 'Não foi possível carregar o fechamento.')
            })
            .catch(() => {
                if (!cancelled) setError('Não foi possível carregar o fechamento.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [params, customInvalid, reloadKey, reloadSignal])

    const periodIsOver = data ? new Date(data.rangeEnd).getTime() < Date.now() : false
    const total = data?.summary.total

    const [exporting, setExporting] = useState<'pdf' | 'xlsx' | 'csv' | null>(null)

    const handleExport = async (format: 'pdf' | 'xlsx' | 'csv') => {
        setExporting(format)
        try {
            await exportMonthClosing(format, params, view)
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Não foi possível exportar o fechamento.')
        } finally {
            setExporting(null)
        }
    }

    /** Tipo como círculo com a inicial (o nome inteiro no `title`), para a tabela caber. */
    const typeBadge = (type: MonthClosingType) => (
        <span
            className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${TYPE_BADGE_CLASS[type]}`}
            title={typeLabel[type]}
        >
            {typeLabel[type].charAt(0)}
        </span>
    )

    /** Custo e lucro só existem com o custo apurado: sem ele, um traço, não zero. */
    const money = (cents: number | null) => (cents == null ? '—' : formatCurrency(cents))

    const periodButton = (mode: PeriodMode, label: string) => (
        <button
            type="button"
            className={`btn btn-sm ${periodMode === mode ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
            onClick={() => setPeriodMode(mode)}
        >
            {label}
        </button>
    )

    return (
        <div className="flex flex-col gap-4">
            <div className="bg-base-100 rounded-2xl border border-base-300/60 p-5 shadow-sm flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <h2 className="font-semibold text-base-content">Fechamento do mês</h2>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost border border-base-300 gap-1"
                            onClick={() => setReloadKey(k => k + 1)}
                            disabled={loading}
                        >
                            <MdRefresh className={loading ? 'animate-spin' : ''} />
                            Atualizar
                        </button>
                        {([
                            ['pdf', 'PDF', MdPictureAsPdf],
                            ['xlsx', 'Excel', MdTableChart],
                            ['csv', 'CSV', MdTextSnippet],
                        ] as const).map(([format, label, Icon]) => (
                            <button
                                key={format}
                                type="button"
                                className="btn btn-sm btn-ghost border border-base-300 gap-1"
                                onClick={() => handleExport(format)}
                                disabled={!data || loading || exporting !== null}
                            >
                                {exporting === format ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <Icon />
                                )}
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-base-content/60">Período</span>
                        <div className="flex gap-2">
                            {periodButton('current', 'Este mês')}
                            {periodButton('month', 'Selecionar mês')}
                            {periodButton('custom', 'Personalizado')}
                        </div>
                    </div>

                    {periodMode === 'month' && (
                        <>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs font-medium text-base-content/60">Mês</span>
                                <select
                                    className="select select-bordered select-sm w-40"
                                    value={selectedMonth}
                                    onChange={e => setSelectedMonth(Number(e.target.value))}
                                >
                                    {MONTHS.map((name, index) => (
                                        <option key={name} value={index + 1}>
                                            {name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs font-medium text-base-content/60">Ano</span>
                                <select
                                    className="select select-bordered select-sm w-28"
                                    value={selectedYear}
                                    onChange={e => setSelectedYear(Number(e.target.value))}
                                >
                                    {years.map(year => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </>
                    )}

                    {periodMode === 'custom' && (
                        <>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs font-medium text-base-content/60">De</span>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm w-40"
                                    value={customStart}
                                    onChange={e => setCustomStart(e.target.value)}
                                />
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs font-medium text-base-content/60">Até</span>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm w-40"
                                    value={customEnd}
                                    onChange={e => setCustomEnd(e.target.value)}
                                />
                            </label>
                        </>
                    )}

                    {data && (
                        <p className="text-sm text-base-content/60 pb-1">
                            {formatDate(data.rangeStart)} a {formatDate(data.rangeEnd)} ·{' '}
                            {data.scope === 'organization'
                                ? `Todas as filiais (${data.companyCount})`
                                : 'Filial atual'}
                        </p>
                    )}
                </div>
                {customInvalid && (
                    <p className="text-sm text-error">A data inicial precisa ser anterior à final.</p>
                )}
            </div>

            {error && <div className="alert alert-error text-sm">{error}</div>}

            {loading && !data && (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            )}

            {data && (
                <div className={`flex flex-col gap-4 ${loading ? 'opacity-60' : ''}`}>
                    {data.truncated && (
                        <div className="alert alert-warning text-sm">
                            O período passou do limite de linhas por tipo e o resultado está incompleto.
                            Escolha um período menor.
                        </div>
                    )}

                    <SalesSummaryTable summary={data.summary} onOutstandingClick={type => setOpenList(type)} />

                    <div className="bg-base-100 rounded-2xl border border-base-300/60 shadow-sm">
                        <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-b border-base-300/60">
                            <div className="flex items-center gap-4 flex-wrap">
                                <h3 className="font-semibold text-base-content">
                                    {view === 'grouped' ? 'Por item' : 'Atendimento por atendimento'}
                                </h3>
                                <div className="flex items-center gap-3 text-xs text-base-content/60">
                                    {data.summary.rows.map(row =>
                                        row.type === 'TOTAL' ? null : (
                                            <span key={row.type} className="flex items-center gap-1">
                                                {typeBadge(row.type)}
                                                {typeLabel[row.type]}
                                            </span>
                                        )
                                    )}
                                </div>
                            </div>
                            <div className="join">
                                <button
                                    type="button"
                                    className={`btn btn-sm join-item gap-1 ${view === 'grouped' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                                    onClick={() => setView('grouped')}
                                >
                                    <MdWorkspaces />
                                    Agrupado
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm join-item gap-1 ${view === 'lines' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                                    onClick={() => setView('lines')}
                                >
                                    <MdViewList />
                                    Linha a linha
                                </button>
                            </div>
                        </div>

                        {/* Sem altura fixa (a página rola, não a tabela) e compacta: cabeçalho
                            quebra em duas linhas e os textos longos quebram, em vez de empurrar
                            a tabela para o lado. */}
                        <div className="overflow-x-auto">
                            <table className="table table-xs [&_th]:whitespace-normal [&_th]:align-bottom [&_td]:py-1.5">
                                <thead>
                                    <tr className="text-base-content/60">
                                        <th>Tipo</th>
                                        {view === 'grouped' ? (
                                            <>
                                                <th>Item</th>
                                                <th className="text-right">Qtd.</th>
                                            </>
                                        ) : (
                                            <>
                                                <th>Nº</th>
                                                <th>Venda</th>
                                                <th>{ClientLabel}</th>
                                                <th>Descrição</th>
                                            </>
                                        )}
                                        <th className="text-right">Vendido</th>
                                        <th className="text-right">Desconto</th>
                                        <th className="text-right">Custo</th>
                                        <th className="text-right">Lucro</th>
                                        {periodIsOver && (
                                            <th className="text-right">Recebido até {formatShortDate(data.rangeEnd)}</th>
                                        )}
                                        <th className="text-right">Recebido</th>
                                        <th className="text-right">A receber</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {view === 'grouped'
                                        ? data.groups.map(g => (
                                              <tr key={`${g.type}:${g.catalogId ?? g.name}`} className="hover">
                                                  <td>{typeBadge(g.type)}</td>
                                                  <td className="min-w-48">{g.name}</td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{g.quantity}</td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(g.sold)}</td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(g.discount)}</td>
                                                  <td
                                                      className="text-right tabular-nums whitespace-nowrap"
                                                      title={g.costUnknownCount ? `${g.costUnknownCount} sem custo apurado` : undefined}
                                                  >
                                                      {formatCurrency(g.cost)}
                                                      {g.costUnknownCount > 0 && <span className="text-warning">*</span>}
                                                  </td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(g.profit)}</td>
                                                  {periodIsOver && (
                                                      <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(g.receivedUntilEnd)}</td>
                                                  )}
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(g.receivedToDate)}</td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(g.outstanding)}</td>
                                              </tr>
                                          ))
                                        : data.lines.map(l => (
                                              <tr key={`${l.type}:${l.id}`} className="hover">
                                                  <td>{typeBadge(l.type)}</td>
                                                  <td>
                                                      <button
                                                          type="button"
                                                          className="link link-secondary tabular-nums whitespace-nowrap"
                                                          onClick={() => openDetail(DETAIL_TYPE[l.type], l.id)}
                                                      >
                                                          #{l.id}
                                                      </button>
                                                  </td>
                                                  <td className="whitespace-nowrap">{formatDate(l.saleDate)}</td>
                                                  <td className="min-w-36">{l.clientName || '—'}</td>
                                                  <td className="min-w-48">
                                                      {l.title || '—'}
                                                      {l.healthPlan && (
                                                          <span className="badge badge-ghost badge-sm ml-2">Convênio</span>
                                                      )}
                                                  </td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(l.sold)}</td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(l.discount)}</td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{money(l.cost)}</td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{money(l.profit)}</td>
                                                  {periodIsOver && (
                                                      <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(l.receivedUntilEnd)}</td>
                                                  )}
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(l.receivedToDate)}</td>
                                                  <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(l.outstanding)}</td>
                                              </tr>
                                          ))}
                                </tbody>
                                {total && (
                                    <tfoot>
                                        <tr className="font-semibold text-base-content bg-base-200/60">
                                            <td>Total</td>
                                            {view === 'grouped' ? (
                                                <>
                                                    <td />
                                                    <td className="text-right tabular-nums whitespace-nowrap">
                                                        {data.groups.reduce((sum, g) => sum + g.quantity, 0)}
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="tabular-nums">{total.count}</td>
                                                    <td />
                                                    <td />
                                                    <td />
                                                </>
                                            )}
                                            <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(total.sold)}</td>
                                            <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(data.totals.discount)}</td>
                                            <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(data.totals.cost)}</td>
                                            <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(data.totals.profit)}</td>
                                            {periodIsOver && (
                                                <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(total.receivedUntilEnd)}</td>
                                            )}
                                            <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(total.receivedToDate)}</td>
                                            <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(total.outstanding)}</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                        {data.totals.costUnknownCount > 0 && (
                            <p className="text-xs text-base-content/60 px-4 py-2 border-t border-base-300/60">
                                <span className="text-warning">*</span> {data.totals.costUnknownCount} de{' '}
                                {data.summary.total.count} atendimentos sem custo apurado: custo e lucro
                                somam só os apurados.
                            </p>
                        )}
                    </div>

                    <div className="bg-base-100 rounded-2xl border border-base-300/60 shadow-sm">
                        <div className="flex items-start gap-3 p-4 border-b border-base-300/60">
                            <div className="p-2 rounded-xl bg-warning/10 text-warning">
                                <MdLinkOff className="text-xl" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-base-content">Pagamentos sem vínculo</h3>
                                <p className="text-sm text-base-content/60">
                                    Pix que entraram no período sem atendimento. Não são venda e ficam fora do
                                    total acima. Quando vinculados, passam a contar dentro do atendimento.
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-base-content/60">
                                    {data.orphans.count} {data.orphans.count === 1 ? 'pagamento' : 'pagamentos'}
                                </p>
                                <p className="font-semibold tabular-nums">{formatCurrency(data.orphans.received)}</p>
                            </div>
                        </div>
                        {data.orphans.count > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr className="text-base-content/60">
                                            <th>Cobrança</th>
                                            <th>Recebido em</th>
                                            <th>Pagador</th>
                                            <th>Filial</th>
                                            <th>Observação</th>
                                            <th className="text-right">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.orphans.rows.map(o => (
                                            <tr key={o.id} className="hover">
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="link link-secondary tabular-nums"
                                                        onClick={() => openDetail('billing', o.id)}
                                                    >
                                                        #{o.id}
                                                    </button>
                                                </td>
                                                <td className="whitespace-nowrap">{formatDate(o.paidAt)}</td>
                                                <td>{o.payerName || '—'}</td>
                                                <td>{o.companyName || '—'}</td>
                                                <td>
                                                    {o.dismissedNote ? (
                                                        <span>Não é de atendimento: {o.dismissedNote}</span>
                                                    ) : (
                                                        <span className="badge badge-warning badge-sm">Sem vínculo</span>
                                                    )}
                                                </td>
                                                <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(o.received)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-sm text-base-content/60 p-4">
                                Nenhum Pix entrou sem atendimento no período.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {data && openList && (
                <OpenItemsModal
                    title={
                        openList === 'TOTAL'
                            ? 'A receber'
                            : `A receber · ${typeLabel[openList]}`
                    }
                    lines={data.lines.filter(
                        l => l.outstanding > 0 && (openList === 'TOTAL' || l.type === openList)
                    )}
                    typeBadge={typeBadge}
                    clientLabel={ClientLabel}
                    onOpen={line => openDetail(DETAIL_TYPE[line.type], line.id)}
                    onOpenBilling={billingId => openDetail('billing', billingId)}
                    onClose={() => setOpenList(null)}
                />
            )}
        </div>
    )
}

/**
 * O que está em aberto, atendimento por atendimento, com o motivo. Nunca agrupado: a
 * pergunta é "quais", e cada linha abre o atendimento (ou a cobrança, pelo motivo) no
 * popup de detalhe, por cima deste modal.
 */
function OpenItemsModal({
    title,
    lines,
    typeBadge,
    clientLabel,
    onOpen,
    onOpenBilling,
    onClose,
}: {
    title: string
    lines: MonthClosingLine[]
    typeBadge: (type: MonthClosingType) => ReactNode
    clientLabel: string
    onOpen: (line: MonthClosingLine) => void
    onOpenBilling: (billingId: number) => void
    onClose: () => void
}) {
    const sorted = [...lines].sort((a, b) => b.outstanding - a.outstanding)
    const total = sorted.reduce((sum, l) => sum + l.outstanding, 0)
    const byReason = new Map<MonthClosingOpenReason, { count: number; amount: number }>()
    for (const line of sorted) {
        const entry = byReason.get(line.openReason) ?? { count: 0, amount: 0 }
        entry.count++
        entry.amount += line.outstanding
        byReason.set(line.openReason, entry)
    }

    // Div com `modal-open`, e não <dialog>: o popup de detalhe abre por cima dele, e um
    // <dialog> aberto com showModal() ficaria na camada superior, na frente do popup.
    return (
        <div className="modal modal-open">
            <div className="modal-box w-11/12 max-w-6xl p-0 flex flex-col max-h-[90vh]">
                <div className="flex items-start justify-between gap-3 p-5 border-b border-base-300/60">
                    <div>
                        <h3 className="font-semibold text-lg">{title}</h3>
                        <p className="text-sm text-base-content/60">
                            {sorted.length} {sorted.length === 1 ? 'atendimento' : 'atendimentos'} ·{' '}
                            {formatCurrency(total)}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {Array.from(byReason.entries()).map(([reason, entry]) => (
                                <span key={reason} className={`badge badge-sm gap-1 ${OPEN_REASON[reason].className}`}>
                                    {OPEN_REASON[reason].label}: {entry.count} · {formatCurrency(entry.amount)}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-circle btn-ghost"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose size={18} />
                    </button>
                </div>

                <div className="overflow-auto">
                    <table className="table table-xs [&_th]:whitespace-normal [&_td]:py-1.5">
                        <thead>
                            <tr className="text-base-content/60">
                                <th>Tipo</th>
                                <th>Nº</th>
                                <th>Venda</th>
                                <th>{clientLabel}</th>
                                <th>Descrição</th>
                                <th>Motivo</th>
                                <th className="text-right">Vendido</th>
                                <th className="text-right">Recebido</th>
                                <th className="text-right">A receber</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(line => (
                                <tr
                                    key={`${line.type}:${line.id}`}
                                    className="hover cursor-pointer"
                                    onClick={() => onOpen(line)}
                                >
                                    <td>{typeBadge(line.type)}</td>
                                    <td className="tabular-nums whitespace-nowrap text-secondary">#{line.id}</td>
                                    <td className="whitespace-nowrap">{formatDate(line.saleDate)}</td>
                                    <td className="min-w-36">{line.clientName || '—'}</td>
                                    <td className="min-w-48">{line.title || '—'}</td>
                                    <td>
                                        {line.billingId ? (
                                            <button
                                                type="button"
                                                className={`badge badge-sm whitespace-nowrap ${OPEN_REASON[line.openReason].className}`}
                                                title={`Abrir a cobrança #${line.billingId}`}
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    onOpenBilling(line.billingId as number)
                                                }}
                                            >
                                                {OPEN_REASON[line.openReason].label}
                                            </button>
                                        ) : (
                                            <span className={`badge badge-sm whitespace-nowrap ${OPEN_REASON[line.openReason].className}`}>
                                                {OPEN_REASON[line.openReason].label}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(line.sold)}</td>
                                    <td className="text-right tabular-nums whitespace-nowrap">{formatCurrency(line.receivedToDate)}</td>
                                    <td className="text-right tabular-nums whitespace-nowrap font-medium">
                                        {formatCurrency(line.outstanding)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
